import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Autocomplete,
  Tooltip,
  Alert,
  Paper,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
  Group as GroupIcon,
  FolderSpecial as ProjectIcon,
  GroupAdd as AssignGroupIcon
} from '@mui/icons-material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import AppHeader from '../../components/AppHeader';

const API_URL1 = process.env.REACT_APP_API_URL1;

export default function ProjectManager({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [, setLoading] = useState(true);

  // Project Dialog State
  const [openProjectDialog, setOpenProjectDialog] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [projectForm, setProjectForm] = useState({
    project_code: '',
    project_name: '',
    description: '',
    status: 'active',
    start_date: '',
    end_date: ''
  });

  // Staff Assignment Dialog State
  const [openStaffDialog, setOpenStaffDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [assignedStaff, setAssignedStaff] = useState([]);
  const [allStaffList, setAllStaffList] = useState([]);
  const [assignForm, setAssignForm] = useState({
    staff: null,
    assignment_start_date: new Date().toISOString().split('T')[0],
    assignment_end_date: ''
  });

  const getBearerToken = () =>
    localStorage.getItem('userToken') ||
    localStorage.getItem('token') ||
    sessionStorage.getItem('userToken') ||
    sessionStorage.getItem('token') ||
    '';

  const getHeaders = useCallback(
    () => ({
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${getBearerToken()}`,
        'X-Tenant': (localStorage.getItem('tenant') || '').trim()
      }
    }),
    []
  );

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL1}/sm/projects`, getHeaders());
      if (res.data?.success) {
        setProjects(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  }, [getHeaders]);

  const fetchAllStaff = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL1}/staff`, getHeaders());
      if (res.data?.success || Array.isArray(res.data)) {
        const staffData = res.data.data || res.data;
        setAllStaffList(Array.isArray(staffData) ? staffData : []);
      }
    } catch (err) {
      console.error('Error fetching staff list:', err);
    }
  }, [getHeaders]);

  useEffect(() => {
    fetchProjects();
    fetchAllStaff();
  }, [fetchProjects, fetchAllStaff]);

  const handleOpenProjectDialog = useCallback((project = null) => {
    if (project) {
      setEditProject(project);
      setProjectForm({
        project_code: project.project_code || '',
        project_name: project.project_name || '',
        description: project.description || '',
        status: project.status || 'active',
        start_date: project.start_date ? project.start_date.split('T')[0] : '',
        end_date: project.end_date ? project.end_date.split('T')[0] : ''
      });
    } else {
      setEditProject(null);
      setProjectForm({
        project_code: '',
        project_name: '',
        description: '',
        status: 'active',
        start_date: '',
        end_date: ''
      });
    }
    setOpenProjectDialog(true);
  }, []);

  const handleSaveProject = async () => {
    if (!projectForm.project_code || !projectForm.project_name) {
      alert('Project Code and Project Name are required.');
      return;
    }

    try {
      if (editProject) {
        await axios.put(`${API_URL1}/sm/projects/${editProject.id}`, projectForm, getHeaders());
      } else {
        await axios.post(`${API_URL1}/sm/projects`, projectForm, getHeaders());
      }
      setOpenProjectDialog(false);
      fetchProjects();
    } catch (err) {
      console.error('Error saving project:', err);
      alert(err.response?.data?.message || 'Failed to save project.');
    }
  };

  const handleDeleteProject = useCallback(
    async (id) => {
      if (window.confirm('Are you sure you want to delete this project?')) {
        try {
          await axios.delete(`${API_URL1}/sm/projects/${id}`, getHeaders());
          fetchProjects();
        } catch (err) {
          console.error('Error deleting project:', err);
          alert(err.response?.data?.message || 'Failed to delete project.');
        }
      }
    },
    [fetchProjects, getHeaders]
  );

  // Manage Staff Assignment
  const handleOpenStaffDialog = useCallback(
    async (project) => {
      setSelectedProject(project);
      setAssignForm({
        staff: null,
        assignment_start_date: new Date().toISOString().split('T')[0],
        assignment_end_date: ''
      });
      try {
        const res = await axios.get(`${API_URL1}/sm/projects/${project.id}/staff`, getHeaders());
        if (res.data?.success) {
          setAssignedStaff(res.data.data || []);
        }
      } catch (err) {
        console.error('Error fetching project staff:', err);
      }
      setOpenStaffDialog(true);
    },
    [getHeaders]
  );

  const handleAssignStaff = async () => {
    if (!assignForm.staff || !assignForm.assignment_start_date) {
      alert('Please select a staff member and start date.');
      return;
    }

    try {
      await axios.post(
        `${API_URL1}/sm/projects/${selectedProject.id}/staff`,
        {
          staff_id: assignForm.staff.id,
          assignment_start_date: assignForm.assignment_start_date,
          assignment_end_date: assignForm.assignment_end_date || null
        },
        getHeaders()
      );

      const res = await axios.get(`${API_URL1}/sm/projects/${selectedProject.id}/staff`, getHeaders());
      if (res.data?.success) setAssignedStaff(res.data.data || []);
      setAssignForm({
        staff: null,
        assignment_start_date: new Date().toISOString().split('T')[0],
        assignment_end_date: ''
      });
      fetchProjects();
    } catch (err) {
      console.error('Error assigning staff:', err);
      alert(err.response?.data?.message || 'Failed to assign staff.');
    }
  };

  const handleRemoveStaffAssignment = async (assignmentId) => {
    if (window.confirm('End active assignment for this staff member?')) {
      try {
        await axios.delete(`${API_URL1}/sm/projects/${selectedProject.id}/staff/${assignmentId}`, getHeaders());
        const res = await axios.get(`${API_URL1}/sm/projects/${selectedProject.id}/staff`, getHeaders());
        if (res.data?.success) setAssignedStaff(res.data.data || []);
        fetchProjects();
      } catch (err) {
        console.error('Error removing assignment:', err);
        alert(err.response?.data?.message || 'Failed to end assignment.');
      }
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    if (setIsLoggedIn) setIsLoggedIn(false);
    navigate('/login');
  };

  // AgGrid Column Definitions matching standard SM tables
  const columnDefs = useMemo(
    () => [
      {
        headerName: 'Project Code',
        field: 'project_code',
        width: 170,
        cellRenderer: (params) => (
          <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: 13 }}>
            {params.value}
          </Typography>
        )
      },
      {
        headerName: 'Project Name',
        field: 'project_name',
        flex: 1.5,
        minWidth: 240,
        cellRenderer: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 0.5, width: '100%', overflow: 'hidden' }}>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: 13, lineHeight: 1.2 }}>
              {params.value}
            </Typography>
            {params.data?.description && (
              <Typography variant="caption" sx={{ color: '#64748b', fontSize: 11, lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {params.data.description}
              </Typography>
            )}
          </Box>
        )
      },
      {
        headerName: 'Status',
        field: 'status',
        width: 130,
        cellRenderer: (params) => {
          const val = (params.value || 'active').toLowerCase();
          const isAct = val === 'active';
          return (
            <Chip
              label={val.toUpperCase()}
              size="small"
              sx={{
                fontWeight: 800,
                fontSize: 10,
                bgcolor: isAct ? '#ecfdf5' : '#fffbeb',
                color: isAct ? '#10b981' : '#f59e0b',
                border: '1px solid',
                borderColor: isAct ? '#d1fae5' : '#fef3c7'
              }}
            />
          );
        }
      },
      {
        headerName: 'Assigned Staff',
        field: 'assigned_staff_count',
        width: 160,
        cellRenderer: (params) => (
          <Chip
            icon={<GroupIcon fontSize="inherit" />}
            label={`${params.value || 0} Staff`}
            size="small"
            onClick={() => handleOpenStaffDialog(params.data)}
            sx={{
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 11,
              bgcolor: '#f5f3ff',
              color: 'primary.main',
              border: '1px solid #ddd6fe',
              '&:hover': { bgcolor: '#ede9fe' }
            }}
          />
        )
      },
      {
        headerName: 'Start Date',
        field: 'start_date',
        width: 130,
        valueFormatter: (params) => (params.value ? params.value.split('T')[0] : '-')
      },
      {
        headerName: 'End Date',
        field: 'end_date',
        width: 130,
        valueFormatter: (params) => (params.value ? params.value.split('T')[0] : '-')
      },
      {
        headerName: 'Actions',
        width: 140,
        pinned: 'right',
        cellRenderer: (params) => (
          <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center" sx={{ height: '100%' }}>
            <Tooltip title="Manage Staff Assignments">
              <IconButton
                size="small"
                onClick={() => handleOpenStaffDialog(params.data)}
                sx={{ color: '#0284c7', '&:hover': { bgcolor: '#e0f2fe' } }}
              >
                <PersonAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit Project">
              <IconButton
                size="small"
                onClick={() => handleOpenProjectDialog(params.data)}
                sx={{ color: 'primary.main', '&:hover': { bgcolor: '#f5f3ff' } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => handleDeleteProject(params.data.id)}
                sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )
      }
    ],
    [handleDeleteProject, handleOpenProjectDialog, handleOpenStaffDialog]
  );

  const activeProjectsCount = useMemo(() => projects.filter((p) => p.status === 'active').length, [projects]);
  const totalAssignedStaff = useMemo(
    () => projects.reduce((acc, p) => acc + (parseInt(p.assigned_staff_count, 10) || 0), 0),
    [projects]
  );

  return (
    <Box sx={{ height: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppHeader onLogout={handleLogout} />

      <Box sx={{ px: 3, py: 2, maxWidth: 1600, mx: 'auto', width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Metric Cards Banner */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Card
            sx={{
              p: 1.5,
              flex: 1,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 10px 0 rgba(0,0,0,0.02)',
              border: '1px solid #f1f5f9'
            }}
          >
            <Box sx={{ p: 1, borderRadius: 2.5, bgcolor: '#f5f3ff', mr: 2, display: 'flex' }}>
              <ProjectIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: 11 }}>
                Active Projects
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                {activeProjectsCount} / {projects.length}
              </Typography>
            </Box>
          </Card>

          <Card
            sx={{
              p: 1.5,
              flex: 1.5,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 10px 0 rgba(0,0,0,0.02)',
              border: '1px solid #f1f5f9'
            }}
          >
            <Box sx={{ p: 1, borderRadius: 2.5, bgcolor: '#ecfdf5', mr: 2, display: 'flex' }}>
              <AssignGroupIcon sx={{ color: '#10b981', fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: 11 }}>
                Total Staff Assigned Across Projects
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                {totalAssignedStaff} Members
              </Typography>
            </Box>
          </Card>
        </Stack>

        <Fade in timeout={500} style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Title Bar & Main Action */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em', mb: 0.2 }}>
                  Project Master Management
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, fontSize: 13 }}>
                  Setup projects and assign staff for project-based attendance and reporting.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenProjectDialog()}
                sx={{
                  bgcolor: 'primary.main',
                  borderRadius: 2,
                  px: 2.5,
                  py: 1,
                  fontWeight: 700,
                  textTransform: 'none',
                  boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}33`,
                  '&:hover': { bgcolor: 'primary.dark' }
                }}
              >
                Define New Project
              </Button>
            </Stack>

            {/* Main AgGrid Data Table */}
            <Paper
              sx={{
                flexGrow: 1,
                minHeight: 0,
                borderRadius: 3,
                overflow: 'hidden',
                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.03)',
                border: '1px solid #f1f5f9',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                <AgGridReact
                  rowData={projects}
                  columnDefs={columnDefs}
                  defaultColDef={{
                    sortable: true,
                    filter: true,
                    resizable: true,
                    cellStyle: { display: 'flex', alignItems: 'center', color: '#334155', fontWeight: 500 }
                  }}
                  pagination={true}
                  paginationPageSize={15}
                  headerHeight={48}
                  rowHeight={58}
                />
              </div>
            </Paper>
          </Box>
        </Fade>
      </Box>

      {/* Create / Edit Project Dialog */}
      <Dialog open={openProjectDialog} onClose={() => setOpenProjectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
          {editProject ? 'Edit Project' : 'Create New Project'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Project Code"
              required
              fullWidth
              size="small"
              value={projectForm.project_code}
              onChange={(e) => setProjectForm((prev) => ({ ...prev, project_code: e.target.value }))}
              placeholder="e.g. PROJ-2026-001"
            />
            <TextField
              label="Project Name"
              required
              fullWidth
              size="small"
              value={projectForm.project_name}
              onChange={(e) => setProjectForm((prev) => ({ ...prev, project_name: e.target.value }))}
              placeholder="e.g. Downtown Building Construction"
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              size="small"
              value={projectForm.description}
              onChange={(e) => setProjectForm((prev) => ({ ...prev, description: e.target.value }))}
            />
            <TextField
              select
              label="Status"
              fullWidth
              size="small"
              value={projectForm.status}
              onChange={(e) => setProjectForm((prev) => ({ ...prev, status: e.target.value }))}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="on_hold">On Hold</MenuItem>
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={projectForm.start_date}
                onChange={(e) => setProjectForm((prev) => ({ ...prev, start_date: e.target.value }))}
              />
              <TextField
                label="End Date"
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                value={projectForm.end_date}
                onChange={(e) => setProjectForm((prev) => ({ ...prev, end_date: e.target.value }))}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenProjectDialog(false)} color="inherit" sx={{ fontWeight: 600 }}>
            Cancel
          </Button>

          <Button onClick={handleSaveProject} variant="contained" sx={{ fontWeight: 700 }}>
            Save Project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff Assignment Dialog */}
      <Dialog open={openStaffDialog} onClose={() => setOpenStaffDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>
          Staff Assignments: {selectedProject?.project_name} ({selectedProject?.project_code})
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
            Assign staff members to this project. Staff assigned will be included when filtering Attendance Records by this project.
          </Alert>

          <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc', borderRadius: 3, borderColor: '#e2e8f0' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#0f172a' }}>
              Assign New Staff Member
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" useFlexGap flexWrap="wrap">
              <Autocomplete
                size="small"
                options={allStaffList}
                getOptionLabel={(option) => `${option.full_name} (${option.employee_number || option.staff_code})`}
                value={assignForm.staff}
                onChange={(e, newValue) => setAssignForm((prev) => ({ ...prev, staff: newValue }))}
                renderInput={(params) => <TextField {...params} label="Select Staff Member" required sx={{ minWidth: 260 }} />}
              />
              <TextField
                label="Start Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={assignForm.assignment_start_date}
                onChange={(e) => setAssignForm((prev) => ({ ...prev, assignment_start_date: e.target.value }))}
                sx={{ minWidth: 150 }}
              />
              <TextField
                label="End Date (Optional)"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={assignForm.assignment_end_date}
                onChange={(e) => setAssignForm((prev) => ({ ...prev, assignment_end_date: e.target.value }))}
                sx={{ minWidth: 150 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAssignStaff}
                sx={{ height: 40, fontWeight: 700, textTransform: 'none' }}
              >
                Assign
              </Button>
            </Stack>
          </Card>

          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1.5, color: '#0f172a' }}>
            Current & Past Assignments ({assignedStaff.length})
          </Typography>

          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>Staff Code</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Staff Name</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>End Date</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignedStaff.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No staff assigned yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  assignedStaff.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.employee_number || row.staff_code}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.staff_name}</TableCell>
                      <TableCell>{row.assignment_start_date ? row.assignment_start_date.split('T')[0] : '-'}</TableCell>
                      <TableCell>{row.assignment_end_date ? row.assignment_end_date.split('T')[0] : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.is_active ? 'ACTIVE' : 'ENDED'}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: 10,
                            bgcolor: row.is_active ? '#ecfdf5' : '#f8fafc',
                            color: row.is_active ? '#16a34a' : '#64748b',
                            border: '1px solid',
                            borderColor: row.is_active ? '#bbf7d0' : '#e2e8f0'
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        {row.is_active ? (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleRemoveStaffAssignment(row.id)}
                            sx={{ fontWeight: 700, textTransform: 'none' }}
                          >
                            End Assignment
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Completed
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenStaffDialog(false)} variant="outlined" sx={{ fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
